import { useState } from 'react';
import { api } from '../services/api';
import type { AngleWithProgress } from '@attack100/shared';

interface CreateAngleModalProps {
  problemId: number;
  existingAngles: AngleWithProgress[];
  onClose: () => void;
  onCreated: () => void;
}

function CreateAngleModal({ problemId, existingAngles, onClose, onCreated }: CreateAngleModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [angleNumber, setAngleNumber] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find next available angle number
  const getNextAngleNumber = () => {
    const usedNumbers = new Set(existingAngles.map(a => a.angle_number));
    for (let i = 1; i <= 100; i++) {
      if (!usedNumbers.has(i)) return i;
    }
    return 1;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    const finalAngleNumber = angleNumber || getNextAngleNumber();

    if (finalAngleNumber < 1 || finalAngleNumber > 100) {
      setError('Angle number must be between 1 and 100');
      return;
    }

    if (existingAngles.some(a => a.angle_number === finalAngleNumber)) {
      setError(`Angle #${finalAngleNumber} already exists`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await api.createAngle(problemId, {
        title,
        description,
        angle_number: finalAngleNumber
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create angle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: '500px', width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
          Add New Angle
        </h2>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="label" htmlFor="angleNumber">
              Angle Number (optional)
            </label>
            <input
              id="angleNumber"
              type="number"
              className="input"
              value={angleNumber}
              onChange={(e) => setAngleNumber(e.target.value ? parseInt(e.target.value) : '')}
              placeholder={`Next available: ${getNextAngleNumber()}`}
              min="1"
              max="100"
            />
          </div>

          <div className="input-group">
            <label className="label" htmlFor="title">
              Title
            </label>
            <input
              id="title"
              type="text"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Market Research"
              required
            />
          </div>

          <div className="input-group">
            <label className="label" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              className="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this angle..."
              required
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Angle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateAngleModal;
